/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PendingInviteClaimer.jsx — mounted ONCE inside the authenticated layout.
 * Reads `pendingInvite` from localStorage (set by InviteWelcome's CTA),
 * calls POST /api/public/invites/:token/claim with the user's JWT, then
 * clears localStorage. Idempotent on the backend.
 *
 * Why localStorage (not sessionStorage): email verification opens in a
 * new tab, so sessionStorage would be lost. 30-day expiry on the stored
 * payload matches the invite token TTL.
 */

import { useEffect, useRef } from 'react';

const API_BASE = (() => {
  const raw = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
  return raw.endsWith('/api') ? raw : `${raw.replace(/\/$/, '')}/api`;
})();

const PENDING_INVITE_KEY = 'pendingInvite';

export default function PendingInviteClaimer() {
  const didRunRef = useRef(false);

  useEffect(() => {
    // Only ever attempt once per page load. Even if the auth context
    // flickers (e.g. a token refresh), we don't double-fire.
    if (didRunRef.current) return;
    didRunRef.current = true;

    let payload = null;
    try {
      const raw = localStorage.getItem(PENDING_INVITE_KEY);
      if (!raw) return;
      payload = JSON.parse(raw);
    } catch (_) {
      // Corrupt entry — clear and bail.
      try { localStorage.removeItem(PENDING_INVITE_KEY); } catch (_) {}
      return;
    }

    if (!payload || !payload.token) return;

    // Expired? Clear and bail.
    if (payload.expiresAt && new Date(payload.expiresAt).getTime() <= Date.now()) {
      try { localStorage.removeItem(PENDING_INVITE_KEY); } catch (_) {}
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return; // Not authenticated yet — leave the payload for next load.

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/invites/${encodeURIComponent(payload.token)}/claim`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
        // Clear on any definitive outcome (success, 404, 410, 400) so we
        // don't keep retrying forever. Keep for retry only on 5xx / network.
        if (res.ok || res.status === 404 || res.status === 410 || res.status === 400) {
          try { localStorage.removeItem(PENDING_INVITE_KEY); } catch (_) {}
        }
      } catch (_) {
        // Network — leave the payload; try again next mount.
      }
    })();
  }, []);

  return null;
}
